import {
   Flex,
   FlexProps,
   Box,
   BoxProps,
   List,
   ListItem,
   ListProps,
   Stack,
   StackProps,
   Text,
   TextProps,
   ListItemProps,
} from '@chakra-ui/react';
import { ReactNode } from 'react';
import { createContext, useContext, useRef } from 'react';

type CatalogVariant = 'flat' | 'nested';

export type CatalogProps = Omit<ListProps, 'variant'> & {
   variant?: CatalogVariant;
};

type CatalogCtx = {
   variant: CatalogVariant;
};

const CatalogCtx = createContext<CatalogCtx>({ variant: 'flat' });

const catalogVariants: Record<CatalogVariant, ListProps> = {
   flat: {},
   nested: {
      backgroundColor: 'gray.100',
      p: 4,
   },
};

export function Catalog({ children, variant = 'flat', ...props }: CatalogProps) {
   return (
      <List {...catalogVariants[variant]} {...props} bgColor="white" borderBottomRadius="md">
         <CatalogCtx.Provider value={{ variant }}>{children}</CatalogCtx.Provider>
      </List>
   );
}

type CatalogItemCtx = {
   id: number;
};

let catalogItemIdSerial = 1;

const CatalogItemCtx = createContext<CatalogItemCtx>({ id: 0 });

type CatalogItemProps = FlexProps & {
   onClick?: () => void;
   children: ReactNode;
};

const catalogItemVariants: Record<CatalogVariant, FlexProps> = {
   flat: {
      p: 3,
   },
   nested: {
      py: 2,
      px: 3,
      borderTop: 'none',
   },
};

export function CatalogItem({ children, onClick, ...props }: CatalogItemProps) {
   const { current: id } = useRef(catalogItemIdSerial++);
   const { variant } = useContext(CatalogCtx);
   return (
      <Flex
         as={onClick ? ListItemButton : ListItem}
         aria-labelledby={`catalog-item-header-${id}`}
         onClick={onClick}
         cursor={onClick && 'pointer'}
         _hover={onClick && { backgroundColor: 'gray.200' }}
         _focus={onClick && { backgroundColor: 'gray.200' }}
         _active={onClick && { backgroundColor: 'gray.300' }}
         transition="background-color 200ms ease"
         borderTop="1px"
         borderTopColor="gray.200"
         _first={{ borderTopWidth: 0 }}
         align="center"
         {...catalogItemVariants[variant]}
         {...props}
      >
         <CatalogItemCtx.Provider value={{ id }}>{children}</CatalogItemCtx.Provider>
      </Flex>
   );
}

export function CatalogItemContent({ children, ...boxProps }: BoxProps) {
   return (
      <Flex ml="3" direction="column" justify="center" {...boxProps}>
         <Box>{children}</Box>
      </Flex>
   );
}

type CatalogItemRightProps = StackProps;

export function CatalogItemRight({ children, ...stackProps }: CatalogItemRightProps) {
   return (
      <Stack flex={1} direction="row" align="center" justify="flex-end" mr={2} {...stackProps}>
         {children}
      </Stack>
   );
}

type CatalogItemActionsProps = BoxProps;

export function CatalogItemActions({ children, ...boxProps }: CatalogItemActionsProps) {
   return (
      <Box onClick={e => e.stopPropagation()} {...boxProps}>
         {children}
      </Box>
   );
}

type CatalogItemHeaderProps = TextProps;

export function CatalogItemHeader({ children, ...props }: CatalogItemHeaderProps) {
   const { id } = useContext(CatalogItemCtx);
   return (
      <Text variant="subtitle" id={`catalog-item-header-${id}`} {...props}>
         {children}
      </Text>
   );
}

function ListItemButton(props: ListItemProps) {
   return <ListItem outline="none" as="button" width="100%" {...props} />;
}
